import { useMultiItemNavigation } from "@utils/multiItemNavigation";
import { useRef } from "react";
import type { FieldValues } from "react-hook-form";
import type { IMultiItemFormStepHandle } from "./FormStep";

export interface IUseMultiItemFormParams<TRaw extends FieldValues, TForm extends FieldValues = TRaw> {
  items: TRaw[];
  // biome-ignore lint/suspicious/noConfusingVoidType: `void` garde assignables les callbacks sans valeur de retour
  onConfirm: (items: TForm[]) => void | number[] | Promise<void | number[]>;
}

export const useMultiItemForm = <TRaw extends FieldValues = FieldValues, TForm extends FieldValues = TRaw>({
  items,
  onConfirm,
}: IUseMultiItemFormParams<TRaw, TForm>) => {
  const nav = useMultiItemNavigation(items);
  const stepRef = useRef<IMultiItemFormStepHandle>(null);

  const mergeIntoItem = (data: FieldValues): TRaw => ({ ...nav.currentItem, ...data }) as TRaw;

  const handleNext = () => {
    stepRef.current?.submit((data) => {
      nav.updateCurrentItem(mergeIntoItem(data));
      nav.goNext();
    });
  };

  const handleConfirm = () => {
    stepRef.current?.submit(async (data) => {
      const current = mergeIntoItem(data);
      nav.updateCurrentItem(current);
      const indexesToKeep = await onConfirm(
        nav.items.map((item, index) => (index === nav.currentIndex ? current : item)) as unknown as TForm[]
      );
      if (indexesToKeep) {
        nav.keepItemsAt(indexesToKeep);
      }
    });
  };

  const handlePrevious = () => {
    nav.updateCurrentItem(mergeIntoItem(stepRef.current?.getValues() ?? {}));
    nav.goPrevious();
  };

  return { ...nav, stepRef, handleNext, handlePrevious, handleConfirm };
};
